'use memo';

import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useEffect,
} from 'react';

import IconButton from '@/components/Button/IconButton';
import { useGlobalAiChatStore } from '@/features/global-ai-chat/store/globalAiChatStore';
import { clampAiChatPanelWidth } from '@/features/global-ai-chat/utils/clampAiChatPanelWidth';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

type AiChatPanelProps = {
  isOpen: boolean;
  /**
   * ヘッダーのタイトル領域に描画されるノード。
   * 文字列だけでなく Popover trigger 等の任意ノードも入れられる。
   */
  title: ReactNode;
  onClose: () => void;
  headerActions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
};

export function AiChatPanel({
  isOpen,
  title,
  onClose,
  headerActions,
  children,
  footer,
}: AiChatPanelProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'component.globalAiChat',
  });
  const panelWidth = useGlobalAiChatStore((state) => state.panelWidth);
  const setPanelWidth = useGlobalAiChatStore((state) => state.setPanelWidth);

  // Esc キーでパネルを閉じる（開いている時のみリスナーを張る）
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // 左端ハンドルのドラッグでパネル幅を変更する（md 以上のみハンドルを表示）。
  // setPointerCapture でハンドル外に出ても pointermove を受け続ける。
  const handleResizePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    const handleElement = event.currentTarget;
    handleElement.setPointerCapture(event.pointerId);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      setPanelWidth(
        clampAiChatPanelWidth(
          window.innerWidth - moveEvent.clientX,
          window.innerWidth,
        ),
      );
    };
    const handlePointerUp = () => {
      handleElement.releasePointerCapture(event.pointerId);
      handleElement.removeEventListener('pointermove', handlePointerMove);
      handleElement.removeEventListener('pointerup', handlePointerUp);
    };
    handleElement.addEventListener('pointermove', handlePointerMove);
    handleElement.addEventListener('pointerup', handlePointerUp);
  };

  return (
    <>
      {isOpen ? (
        <div
          aria-hidden
          onClick={onClose}
          className="fixed inset-0 z-50 bg-transparent"
        />
      ) : null}
      <aside
        // React 18 / @types/react 18.3 では `inert` がIntrinsic Attributesに無いため spread で渡す
        {...(isOpen ? {} : { inert: '' })}
        aria-hidden={!isOpen}
        style={{ '--ai-chat-panel-width': `${panelWidth}px` } as CSSProperties}
        className={cn(
          // モバイルはフルスクリーン、md 以上で右側ドロワー（幅はドラッグで可変・永続化）
          'fixed inset-0 z-60 flex h-full w-full flex-col bg-neutral-fill shadow-lg',
          'md:inset-y-0 md:left-auto md:right-0 md:w-[var(--ai-chat-panel-width)] md:border-l md:border-neutral-muted-border',
          'transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('resizeHandleAriaLabel')}
          onPointerDown={handleResizePointerDown}
          className={cn(
            'absolute inset-y-0 left-0 z-10 hidden w-1.5 cursor-col-resize touch-none md:block',
            'hover:bg-primary-fill active:bg-primary-fill',
          )}
        />
        <header className="flex items-center gap-2 border-b border-neutral-muted-border px-4 py-3">
          <div className="min-w-0 flex-1">{title}</div>
          <div className="flex shrink-0 items-center gap-1">
            {headerActions}
            <IconButton
              icon="PanelRightCloseIcon"
              variant="mutedGhost"
              size="Small"
              aria-label={t('closePanelAriaLabel')}
              onClick={onClose}
            />
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer ? (
          <footer className="border-t border-neutral-muted-border px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </aside>
    </>
  );
}
